import { useState, useEffect } from 'react';
import { MessageSquare, Send, EyeOff, Eye, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { commentsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

export function CommentSection({ contentType, contentId, authorId = null }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user, isAuthenticated, isAdmin } = useAuth();

  // Check if current user can moderate (admin or author of content)
  const canModerate = isAdmin || (user?.role === 'author' && authorId === user?.id);

  useEffect(() => {
    fetchComments();
  }, [contentType, contentId]);

  const fetchComments = async () => {
    try {
      const response = await commentsAPI.getComments(contentType, contentId);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to load comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await commentsAPI.createComment(contentType, contentId, newComment.trim());
      setNewComment('');
      fetchComments();
      toast.success('Comment posted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleHide = async (commentId, isHidden) => {
    try {
      if (isHidden) {
        await commentsAPI.unhideComment(commentId);
        toast.success('Comment restored');
      } else {
        await commentsAPI.hideComment(commentId);
        toast.success('Comment hidden');
      }
      fetchComments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update comment');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    // Less than 1 minute
    if (diff < 60000) return 'Just now';
    // Less than 1 hour
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    // Less than 24 hours
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    // Less than 7 days
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} days ago`;
    
    return date.toLocaleDateString();
  };

  // Filter comments - show hidden only to moderators
  const visibleComments = comments.filter(c => !c.hidden || canModerate);

  return (
    <section className="mt-12 pt-8 border-t border-border" data-testid="comment-section">
      <div className="flex items-center gap-2 mb-6">
        <MessageSquare className="h-5 w-5 text-accent" />
        <h3 className="font-heading text-xl font-semibold text-foreground">
          Comments ({visibleComments.filter(c => !c.hidden).length})
        </h3>
      </div>

      {/* Comment Form */}
      {isAuthenticated ? (
        <form onSubmit={handleSubmit} className="mb-8">
          <div className="flex gap-3">
            <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center shrink-0">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-accent font-semibold text-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Share your thoughts..."
                className="w-full p-3 border border-border rounded-md bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-accent/50"
                rows={3}
                data-testid="comment-input"
              />
              <div className="flex justify-end mt-2">
                <Button 
                  type="submit" 
                  disabled={!newComment.trim() || submitting}
                  data-testid="submit-comment"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Post Comment
                </Button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="mb-8 p-4 bg-muted/30 rounded-md text-center">
          <p className="text-muted-foreground mb-2">Sign in to join the conversation</p>
          <Link to="/login">
            <Button variant="outline" size="sm">Sign In</Button>
          </Link>
        </div>
      )}

      {/* Comments List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3 animate-pulse">
              <div className="w-10 h-10 bg-muted rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-muted rounded w-32 mb-2"></div>
                <div className="h-16 bg-muted rounded"></div>
              </div>
            </div>
          ))}
        </div>
      ) : visibleComments.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-30" />
          <p>No comments yet. Be the first to share your thoughts!</p>
        </div>
      ) : (
        <div className="space-y-6">
          {visibleComments.map((comment) => (
            <div 
              key={comment.id} 
              className={`flex gap-3 ${comment.hidden ? 'opacity-50' : ''}`}
              data-testid={`comment-${comment.id}`}
            >
              <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center shrink-0">
                {comment.user_picture ? (
                  <img 
                    src={comment.user_picture} 
                    alt={comment.user_name} 
                    className="w-full h-full rounded-full object-cover" 
                  />
                ) : (
                  <span className="text-accent font-semibold text-sm">
                    {comment.user_name?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-foreground">{comment.user_name}</span>
                  <span className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</span>
                  {comment.hidden && (
                    <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">Hidden</span>
                  )}
                </div>
                <p className="text-foreground whitespace-pre-wrap">{comment.text}</p>
                
                {/* Moderation Actions */}
                {canModerate && (
                  <div className="mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => handleToggleHide(comment.id, comment.hidden)}
                    >
                      {comment.hidden ? (
                        <>
                          <Eye className="h-4 w-4 mr-1" />
                          Restore
                        </>
                      ) : (
                        <>
                          <EyeOff className="h-4 w-4 mr-1" />
                          Hide
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default CommentSection;
